import { useEffect, useState, useMemo, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Search, ArrowUpDown, ArrowUp, ArrowDown, ShieldCheck, AlertTriangle, Info, Menu, X, Building2, Lock } from "lucide-react";
import SalaryBar from "../components/SalaryBar";
import Tooltip from "../components/Tooltip";
import FloatingActionButton from "../components/FloatingActionButton";
import ThemeToggle from "../components/ThemeToggle";
import { motion } from "framer-motion";

// Configure axios for better error logging
axios.interceptors.response.use(
  response => {
    console.log(`[API] Success: ${response.config.url}`);
    return response;
  },
  error => {
    if (!axios.isCancel(error)) {
      console.error(`[API] Error: ${error.config?.url || 'unknown'} - ${error.message}`);
    }
    return Promise.reject(error);
  }
);

// Number of rankings visible without login
const FREE_RANKINGS_LIMIT = 4;

const formatCurrency = (value) => {
  if (value >= 10000000) {
    return `${(value / 10000000).toFixed(2)} Cr`;
  } else if (value >= 100000) {
    return `${(value / 100000).toFixed(2)} L`;
  }
  return value.toLocaleString('en-IN');
};

// Get max CTC for salary bar calculations
const getMaxCtc = (rankings) => {
  if (!rankings || rankings.length === 0) return 5000000;
  return Math.max(...rankings.map(r => r.avg_ctc)) * 1.1;
};

// Calculate confidence score based on number of reports and verification rate
const getConfidenceScore = (placementCount, verifiedCount = 0) => {
  // Base score from report count
  let score = 0;
  if (placementCount >= 50) score = 95;
  else if (placementCount >= 30) score = 85;
  else if (placementCount >= 15) score = 70;
  else if (placementCount >= 10) score = 60;
  else if (placementCount >= 5) score = 45;
  else score = 30;
  
  return score;
};

const ConfidenceBadge = ({ score, count }) => {
  let level, icon, label;
  
  if (score >= 70) {
    level = "high";
    icon = <ShieldCheck className="h-3 w-3" />;
    label = "High";
  } else if (score >= 50) {
    level = "medium";
    icon = <Info className="h-3 w-3" />;
    label = "Medium";
  } else {
    level = "low";
    icon = <AlertTriangle className="h-3 w-3" />;
    label = "Low";
  }
  
  return (
    <Tooltip content={`Based on ${count} reports`}>
      <div className={`confidence-badge confidence-${level}`}>
        {icon}
        <span>{score}%</span>
      </div>
    </Tooltip>
  );
};

const Dashboard = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Show full rankings if user is logged in OR was previously logged in (session expired)
  // This prevents rankings from "disappearing" when JWT expires during a session
  const wasAuthenticated = localStorage.getItem('transplacement_was_authenticated') === 'true';
  const showFullRankings = !!user || wasAuthenticated;

  // Initialize rankings from localStorage cache — no TTL expiry.
  // Cache is always shown while fresh data fetches in the background.
  // This prevents rankings from disappearing when the backend is slow/sleeping.
  const getInitialRankings = () => {
    try {
      const cached = localStorage.getItem('transplacement_rankings_cache');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed.data?.length > 0) return parsed.data;
      }
    } catch (e) {
      console.error("Error reading cache:", e);
    }
    return [];
  };
  
  const [rankings, setRankings] = useState(getInitialRankings);
  const [programs, setPrograms] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [activeAutocompleteIndex, setActiveAutocompleteIndex] = useState(-1);
  const searchRef = useRef(null);
  const cachedRankings = useRef({}); // Cache rankings by category+program
  const fetchIdRef = useRef(0); // Track fetch sequence to prevent stale updates
  
  // Filters
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || "");
  const [selectedProgram, setSelectedProgram] = useState(searchParams.get('program') || "all");
  const [sortBy, setSortBy] = useState("rank");
  const [sortOrder, setSortOrder] = useState("asc");

  const categories = ["Engineering", "Business", "Arts", "Science", "Law"];
  
  // Save rankings to localStorage whenever they change
  useEffect(() => {
    if (rankings.length > 0) {
      try {
        localStorage.setItem('transplacement_rankings_cache', JSON.stringify({
          data: rankings,
          timestamp: Date.now(),
          category: selectedCategory,
          program: selectedProgram
        }));
      } catch (e) {
        console.error("Error saving cache:", e);
      }
    }
  }, [rankings, selectedCategory, selectedProgram]);

  useEffect(() => {
    const cacheKey = `${selectedCategory || 'all'}_${selectedProgram || 'all'}`;
    const currentFetchId = ++fetchIdRef.current;
    
    const abortController = new AbortController();
    const previousRankings = [...rankings]; // Deep copy to preserve
    
    console.log(`[Rankings] Fetching for category=${selectedCategory || 'all'}, program=${selectedProgram || 'all'}, fetchId=${currentFetchId}`);
    
    const fetchData = async (isRetry = false, retryAttempt = 0) => {
      if (!isRetry && previousRankings.length === 0) {
        setLoading(true);
      }
      setError(null);
      
      try {
        // Fetch rankings first (most important) - longer timeout for production
        const rankingsRes = await axios.get(`${API}/rankings`, {
          params: {
            category: selectedCategory || undefined,
            program_id: selectedProgram === "all" ? undefined : selectedProgram || undefined,
            limit: 100
          },
          timeout: 45000, // 45 seconds for slow production connections
          signal: abortController.signal,
          headers: {
            'Cache-Control': 'no-cache'
          }
        });
        
        // Check if still valid request
        if (currentFetchId !== fetchIdRef.current) {
          console.log(`[Rankings] Ignoring stale response, fetchId=${currentFetchId}, current=${fetchIdRef.current}`);
          return;
        }
        
        // Update rankings if valid data
        if (rankingsRes.data && Array.isArray(rankingsRes.data)) {
          console.log(`[Rankings] Got ${rankingsRes.data.length} results for ${cacheKey}`);
          
          if (rankingsRes.data.length > 0) {
            setRankings(rankingsRes.data);
            cachedRankings.current[cacheKey] = rankingsRes.data;
          } else {
            // Empty from backend — only clear if we never had data for this filter.
            // If we had previous data, keep it; a spurious empty response from a
            // cold-start or transient DB issue should not wipe the table.
            console.log(`[Rankings] Empty results for ${cacheKey}`);
            if (previousRankings.length === 0) {
              setRankings([]);
              cachedRankings.current[cacheKey] = [];
            } else {
              console.log(`[Rankings] Keeping previous data — ignoring empty response`);
            }
          }
        }
        
        // Fetch programs and colleges in parallel (non-critical)
        try {
          const [programsRes, collegesRes] = await Promise.all([
            axios.get(`${API}/programs`, { timeout: 15000, signal: abortController.signal }),
            axios.get(`${API}/colleges`, { timeout: 15000, signal: abortController.signal })
          ]);
          
          if (currentFetchId === fetchIdRef.current) {
            if (programsRes.data && Array.isArray(programsRes.data)) {
              setPrograms(programsRes.data);
            }
            if (collegesRes.data && Array.isArray(collegesRes.data)) {
              setColleges(collegesRes.data);
            }
          }
        } catch (secondaryErr) {
          console.log("[Rankings] Secondary data fetch failed (non-critical):", secondaryErr.message);
        }
        
        setLoading(false);
        
      } catch (err) {
        // Ignore aborted requests
        if (axios.isCancel(err) || err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
          console.log(`[Rankings] Request cancelled for fetchId=${currentFetchId}`);
          return;
        }
        
        if (currentFetchId !== fetchIdRef.current) {
          return;
        }
        
        console.error(`[Rankings] Error fetching data:`, err.message);
        
        // Priority 1: Use cached data for this category
        if (cachedRankings.current[cacheKey]?.length > 0) {
          console.log(`[Rankings] Using cached data for ${cacheKey}, count=${cachedRankings.current[cacheKey].length}`);
          setRankings(cachedRankings.current[cacheKey]);
          setLoading(false);
        }
        // Priority 2: Keep previous rankings
        else if (previousRankings.length > 0) {
          console.log(`[Rankings] Keeping previous rankings, count=${previousRankings.length}`);
          setRankings(previousRankings);
          setLoading(false);
        }
        // Priority 3: Retry up to 3 times
        else if (retryAttempt < 3) {
          const delay = Math.pow(2, retryAttempt + 1) * 1000;
          console.log(`[Rankings] Retrying in ${delay/1000}s (attempt ${retryAttempt + 1}/3)`);
          setTimeout(() => {
            if (currentFetchId === fetchIdRef.current) {
              fetchData(true, retryAttempt + 1);
            }
          }, delay);
          return;
        }
        // Priority 4: Show error
        else {
          console.error(`[Rankings] All retries failed for ${cacheKey}`);
          setError("Unable to load rankings. Please refresh the page.");
          setLoading(false);
        }
      }
    };
    
    // Check cache first
    if (cachedRankings.current[cacheKey]?.length > 0) {
      console.log(`[Rankings] Using cached data immediately for ${cacheKey}`);
      setRankings(cachedRankings.current[cacheKey]);
      setLoading(false);
      fetchData(true); // Background refresh
    } else if (previousRankings.length > 0) {
      // Keep showing previous while loading
      fetchData(false);
    } else {
      setLoading(true);
      fetchData(false);
    }
    
    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, selectedProgram]);

  // Close autocomplete when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowAutocomplete(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Autocomplete suggestions
  const autocompleteSuggestions = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return [];
    const query = searchQuery.toLowerCase();
    return colleges
      .filter(c => c.name.toLowerCase().includes(query) || c.short_name?.toLowerCase().includes(query))
      .slice(0, 5);
  }, [searchQuery, colleges]);

  // Handle search input
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setShowAutocomplete(true);
    setActiveAutocompleteIndex(-1);
  };

  // Handle keyboard navigation in autocomplete
  const handleSearchKeyDown = (e) => {
    if (!showAutocomplete || autocompleteSuggestions.length === 0) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveAutocompleteIndex(prev => 
        prev < autocompleteSuggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveAutocompleteIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter' && activeAutocompleteIndex >= 0) {
      e.preventDefault();
      navigate(`/college/${autocompleteSuggestions[activeAutocompleteIndex].college_id}`);
    } else if (e.key === 'Escape') {
      setShowAutocomplete(false);
    }
  };

  // Mobile navigation handler
  const handleMobileNav = (path) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  // Get max CTC for salary bars
  const maxCtc = useMemo(() => getMaxCtc(rankings), [rankings]);

  // Filter and sort rankings
  const filteredRankings = useMemo(() => {
    let filtered = [...rankings];
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => 
        r.college_name.toLowerCase().includes(query) ||
        r.location.toLowerCase().includes(query) ||
        r.program_name.toLowerCase().includes(query) ||
        r.program_short?.toLowerCase().includes(query)
      );
    }
    
    // Sort
    filtered.sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      if (sortOrder === "asc") return aVal - bVal;
      return bVal - aVal;
    });
    
    // Add confidence scores but preserve original rank from API
    return filtered.map((r) => ({ 
      ...r, 
      confidence: getConfidenceScore(r.placement_count)
    }));
  }, [rankings, searchQuery, sortBy, sortOrder]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("desc");
    }
  };

  const handleCategoryFilter = (category) => {
    const newCategory = category === selectedCategory ? "" : category;
    setSelectedCategory(newCategory);
    setSelectedProgram("all");
    
    const newParams = {};
    if (newCategory) newParams.category = newCategory;
    setSearchParams(newParams);
  };

  const handleProgramChange = (programId) => {
    setSelectedProgram(programId);
    
    const newParams = {};
    if (selectedCategory) newParams.category = selectedCategory;
    if (programId && programId !== "all") newParams.program = programId;
    setSearchParams(newParams);
  };

  const filteredPrograms = useMemo(() => {
    if (!selectedCategory) return programs;
    return programs.filter(p => p.category === selectedCategory);
  }, [programs, selectedCategory]);

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return <ArrowUpDown className="h-3 w-3 opacity-50" />;
    return sortOrder === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
  };

  // Get current display title
  const getPageTitle = () => {
    if (selectedProgram && selectedProgram !== "all") {
      const prog = programs.find(p => p.program_id === selectedProgram);
      return prog ? `${prog.name} Rankings` : "College Rankings";
    }
    if (selectedCategory) {
      return `${selectedCategory} Rankings`;
    }
    return "College Rankings";
  };

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="border-b border-border/50 bg-white/70 dark:bg-slate-950/70 backdrop-blur-xl sticky top-0 z-[60]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <Link to="/" className="font-heading text-lg sm:text-xl font-semibold tracking-tight" data-testid="logo">
              Transplacement
            </Link>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-4">
              <Link to="/dashboard" className="text-sm font-medium text-primary" data-testid="nav-rankings">
                Rankings
              </Link>
              {user ? (
                <>
                  <Link to="/submit" className="text-sm font-medium hover:text-primary/80" data-testid="nav-submit">
                    Submit
                  </Link>
                  <Link to="/profile" data-testid="nav-profile">
                    <Button variant="outline" className="h-10 px-4 text-sm font-medium rounded-full">
                      Profile
                    </Button>
                  </Link>
                </>
              ) : (
                <Button onClick={() => login('/dashboard')} className="h-10 px-6 text-sm font-medium rounded-full" data-testid="nav-login-btn">
                  Sign in
                </Button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 -mr-2 touch-target"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 bg-white dark:bg-slate-950 z-50 md:hidden">
          <div className="flex flex-col p-6 pt-20">
            <button 
              onClick={() => handleMobileNav('/')}
              className="py-4 text-lg font-medium border-b border-border text-left"
            >
              Home
            </button>
            <button 
              onClick={() => handleMobileNav('/dashboard')}
              className="py-4 text-lg font-medium border-b border-border text-left text-primary"
            >
              Rankings
            </button>
            {user ? (
              <>
                <button 
                  onClick={() => handleMobileNav('/submit')}
                  className="py-4 text-lg font-medium border-b border-border text-left"
                >
                  Submit Placement
                </button>
                <button 
                  onClick={() => handleMobileNav('/profile')}
                  className="py-4 text-lg font-medium border-b border-border text-left"
                >
                  My Profile
                </button>
              </>
            ) : (
              <button 
                onClick={() => { setMobileMenuOpen(false); login('/dashboard'); }}
                className="mt-6 h-12 bg-primary text-primary-foreground font-medium text-sm rounded-full"
              >
                Sign in with Google
              </button>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <section className="border-b border-border py-6 sm:py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight">
            {getPageTitle()}
          </h1>
          <p className="text-muted-foreground mt-1 sm:mt-2 text-sm sm:text-base">
            Ranked by average placement CTC reported by students
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-border py-3 sm:py-4 bg-secondary sticky top-14 sm:top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex flex-col gap-3 sm:gap-4">
            {/* Row 1: Search and Category */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              {/* Search with Autocomplete */}
              <div className="relative flex-1 max-w-md" ref={searchRef}>
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search colleges..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyDown}
                  onFocus={() => setShowAutocomplete(true)}
                  className="pl-10 h-10 rounded-full"
                  data-testid="search-input"
                />
                
                {/* Autocomplete Dropdown */}
                {showAutocomplete && autocompleteSuggestions.length > 0 && (
                  <div className="autocomplete-dropdown">
                    {autocompleteSuggestions.map((college, index) => (
                      <div
                        key={college.college_id}
                        className={`autocomplete-item ${index === activeAutocompleteIndex ? 'active' : ''}`}
                        onClick={() => navigate(`/college/${college.college_id}`)}
                      >
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="font-medium text-sm">{college.name}</div>
                          <div className="text-xs text-muted-foreground">{college.location}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Category Filter - Scrollable on mobile */}
              <div className="filter-scroll">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => handleCategoryFilter(cat)}
                    className={`filter-chip ${selectedCategory === cat ? 'active' : ''}`}
                    data-testid={`filter-${cat.toLowerCase()}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Row 2: Program Filter (shows when category selected) */}
            {selectedCategory && filteredPrograms.length > 0 && (
              <motion.div 
                className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 pt-2 border-t border-border/50"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
              >
                <span className="text-xs text-muted-foreground flex-shrink-0 uppercase tracking-wide font-medium">Program:</span>
                <div className="filter-scroll">
                  <button
                    onClick={() => handleProgramChange("all")}
                    className={`filter-chip text-xs ${selectedProgram === "all" ? 'active' : ''}`}
                    data-testid="program-all"
                  >
                    All {selectedCategory}
                  </button>
                  {filteredPrograms.map((prog) => (
                    <button
                      key={prog.program_id}
                      onClick={() => handleProgramChange(prog.program_id)}
                      className={`filter-chip text-xs ${selectedProgram === prog.program_id ? 'active' : ''}`}
                      data-testid={`program-${prog.program_id}`}
                    >
                      {prog.short_name}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Rankings Table */}
      <section className="py-4 sm:py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="mb-3 text-xs sm:text-sm text-muted-foreground flex items-center gap-2">
            <span>Showing {filteredRankings.length} results</span>
            {loading && filteredRankings.length > 0 && (
              <span className="inline-flex items-center gap-1 text-primary">
                <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-xs">Updating...</span>
              </span>
            )}
            {loading && filteredRankings.length === 0 && (
              <span className="text-xs text-muted-foreground">Loading data...</span>
            )}
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block relative">
            <div className="table-wrapper border border-border">
              <table className="w-full min-w-[600px] table-fixed">
              <colgroup>
                <col className="w-16" />
                <col className="w-auto" />
                <col className="w-28" />
                <col className="w-28" />
                <col className="w-24" />
                <col className="w-20 hidden lg:table-column" />
                <col className="w-24 hidden xl:table-column" />
              </colgroup>
              <thead className="bg-secondary">
                <tr>
                  <th className="text-left font-heading uppercase text-xs tracking-wider text-muted-foreground p-4">
                    Rank
                  </th>
                  <th className="text-left font-heading uppercase text-xs tracking-wider text-muted-foreground p-4">
                    College
                  </th>
                  <th className="text-left font-heading uppercase text-xs tracking-wider text-muted-foreground p-4 hidden md:table-cell">
                    Location
                  </th>
                  <th className="text-left font-heading uppercase text-xs tracking-wider text-muted-foreground p-4">
                    Program
                  </th>
                  <th 
                    className="text-center font-heading uppercase text-xs tracking-wider text-muted-foreground p-4 cursor-pointer select-none hover:text-foreground transition-colors"
                    onClick={() => handleSort("avg_ctc")}
                    data-testid="sort-avg-ctc"
                  >
                    <span className="flex items-center justify-center gap-2">
                      Avg CTC <SortIcon column="avg_ctc" />
                    </span>
                  </th>
                  <th 
                    className="text-center font-heading uppercase text-xs tracking-wider text-muted-foreground p-4 cursor-pointer select-none hover:text-foreground transition-colors"
                    onClick={() => handleSort("max_ctc")}
                    data-testid="sort-max-ctc"
                  >
                    <span className="flex items-center justify-center gap-2">
                      Max CTC <SortIcon column="max_ctc" />
                    </span>
                  </th>
                  <th 
                    className="text-center font-heading uppercase text-xs tracking-wider text-muted-foreground p-4 cursor-pointer select-none hover:text-foreground transition-colors hidden lg:table-cell"
                    onClick={() => handleSort("placement_count")}
                    data-testid="sort-reports"
                  >
                    <span className="flex items-center justify-center gap-2">
                      Reports <SortIcon column="placement_count" />
                    </span>
                  </th>
                  <th className="text-center font-heading uppercase text-xs tracking-wider text-muted-foreground p-4 hidden xl:table-cell">
                    Confidence
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading && filteredRankings.length === 0 ? (
                  [...Array(10)].map((_, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="p-4"><div className="skeleton h-8 w-8" /></td>
                      <td className="p-4"><div className="skeleton h-6 w-48" /></td>
                      <td className="p-4 hidden md:table-cell"><div className="skeleton h-6 w-32" /></td>
                      <td className="p-4"><div className="skeleton h-6 w-24" /></td>
                      <td className="p-4 text-right"><div className="skeleton h-6 w-20 ml-auto" /></td>
                      <td className="p-4 text-right"><div className="skeleton h-6 w-20 ml-auto" /></td>
                      <td className="p-4 text-right hidden lg:table-cell"><div className="skeleton h-6 w-12 ml-auto" /></td>
                      <td className="p-4 text-center hidden xl:table-cell"><div className="skeleton h-6 w-16 mx-auto" /></td>
                    </tr>
                  ))
                ) : error && filteredRankings.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center">
                      <div className="text-red-500 mb-2">{error}</div>
                      <button 
                        onClick={() => window.location.reload()} 
                        className="text-sm text-primary hover:underline"
                      >
                        Click to retry
                      </button>
                    </td>
                  </tr>
                ) : filteredRankings.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-muted-foreground text-sm">
                      No results found. Try adjusting your filters.
                    </td>
                  </tr>
                ) : (
                  (showFullRankings ? filteredRankings : filteredRankings.slice(0, FREE_RANKINGS_LIMIT)).map((ranking, index) => (
                    <tr 
                      key={`${ranking.college_id}-${ranking.program_id}`}
                      className="border-t border-border table-row-hover cursor-pointer animate-fade-in-up"
                      style={{ animationDelay: `${index * 0.03}s` }}
                      onClick={() => navigate(`/college/${ranking.college_id}?program=${ranking.program_id}`)}
                      data-testid={`ranking-row-${index}`}
                    >
                      <td className="p-4">
                        <div className={`rank-badge ${ranking.rank === 1 ? 'gold' : ranking.rank === 2 ? 'silver' : ranking.rank === 3 ? 'bronze' : 'default'}`}>
                          {ranking.rank}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="font-medium">{ranking.college_name}</div>
                        <span className="inline-block px-2 py-0.5 text-xs bg-secondary border border-border rounded-full mt-1">
                          {ranking.type}
                        </span>
                      </td>
                      <td className="p-4 hidden md:table-cell">
                        <span className="text-sm text-muted-foreground">{ranking.location}</span>
                      </td>
                      <td className="p-4">
                        <span className="font-medium text-sm">{ranking.program_short}</span>
                        <div className="text-xs text-muted-foreground">{ranking.category}</div>
                      </td>
                      <td className="p-4 text-center">
                        <Tooltip content={`Fixed + Variable + Bonuses`}>
                          <div className="flex flex-col items-center">
                            <span className="font-mono font-bold text-lg">{formatCurrency(ranking.avg_ctc)}</span>
                            <SalaryBar value={ranking.avg_ctc} maxValue={maxCtc} className="mt-1.5 w-20" />
                          </div>
                        </Tooltip>
                      </td>
                      <td className="p-4 text-center">
                        <span className="font-mono text-muted-foreground">{formatCurrency(ranking.max_ctc)}</span>
                      </td>
                      <td className="p-4 text-center hidden lg:table-cell">
                        <span className="font-mono">{ranking.placement_count}</span>
                      </td>
                      <td className="p-4 text-center hidden xl:table-cell">
                        <ConfidenceBadge score={ranking.confidence} count={ranking.placement_count} />
                      </td>
                    </tr>
                  ))
                )}
                
                {/* Blurred rows for non-logged in users - rendered outside main condition */}
                {!showFullRankings && !loading && filteredRankings.length > FREE_RANKINGS_LIMIT &&
                  filteredRankings.slice(FREE_RANKINGS_LIMIT, FREE_RANKINGS_LIMIT + 5).map((ranking, index) => (
                    <tr 
                      key={`blur-${ranking.college_id}-${ranking.program_id}`}
                      className="border-t border-border pointer-events-none select-none"
                      style={{ filter: `blur(${4 + index * 1}px)` }}
                    >
                      <td className="p-4">
                        <div className="rank-badge default">{ranking.rank}</div>
                      </td>
                      <td className="p-4">
                        <div className="font-medium">{ranking.college_name}</div>
                      </td>
                      <td className="p-4 hidden md:table-cell">
                        <span className="text-sm text-muted-foreground">{ranking.location}</span>
                      </td>
                      <td className="p-4">
                        <span className="font-medium text-sm">{ranking.program_short}</span>
                      </td>
                      <td className="p-4 text-center">
                        <span className="font-mono font-bold text-lg">{formatCurrency(ranking.avg_ctc)}</span>
                      </td>
                      <td className="p-4 text-center">
                        <span className="font-mono text-muted-foreground">{formatCurrency(ranking.max_ctc)}</span>
                      </td>
                      <td className="p-4 text-center hidden lg:table-cell">
                        <span className="font-mono">{ranking.placement_count}</span>
                      </td>
                      <td className="p-4 text-center hidden xl:table-cell">
                        <span className="text-muted-foreground">--</span>
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
            </div>
            
            {/* Desktop Login Overlay */}
            {!showFullRankings && filteredRankings.length > FREE_RANKINGS_LIMIT && (
              <div className="absolute bottom-0 left-0 right-0 h-[200px] flex items-end justify-center bg-gradient-to-t from-background via-background/95 to-transparent pointer-events-none">
                <div className="pointer-events-auto text-center p-6 mb-4 rounded-2xl bg-background/95 backdrop-blur-sm border border-border shadow-xl">
                  <Lock className="h-8 w-8 mx-auto text-primary mb-3" />
                  <h3 className="font-heading text-lg font-semibold mb-1">
                    Sign in to see all {filteredRankings.length} rankings
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Unlock {filteredRankings.length - FREE_RANKINGS_LIMIT}+ more colleges with verified placement data
                  </p>
                  <Button 
                    onClick={() => login('/dashboard')}
                    className="h-10 px-6 rounded-full"
                    data-testid="desktop-rankings-login-btn"
                  >
                    Sign In with Google
                  </Button>
                </div>
              </div>
            )}
          </div>
          
          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {loading ? (
              [...Array(10)].map((_, i) => (
                <div key={i} className="p-4 rounded-xl border border-border bg-background">
                  <div className="flex items-center gap-3">
                    <div className="skeleton h-10 w-10 rounded-full" />
                    <div className="flex-1">
                      <div className="skeleton h-4 w-32 mb-2" />
                      <div className="skeleton h-3 w-24" />
                    </div>
                    <div className="skeleton h-6 w-16" />
                  </div>
                </div>
              ))
            ) : filteredRankings.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm border border-border rounded-xl">
                No results found. Try adjusting your filters.
              </div>
            ) : (
              <>
                {(showFullRankings ? filteredRankings : filteredRankings.slice(0, FREE_RANKINGS_LIMIT)).map((ranking, index) => (
                  <motion.div
                    key={`${ranking.college_id}-${ranking.program_id}`}
                    className="p-4 rounded-xl border border-border bg-background active:scale-[0.99] cursor-pointer"
                    onClick={() => navigate(`/college/${ranking.college_id}?program=${ranking.program_id}`)}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    data-testid={`ranking-card-${index}`}
                  >
                    <div className="flex items-center gap-3">
                      {/* Rank Badge */}
                      <div className={`w-10 h-10 flex-shrink-0 flex items-center justify-center font-mono text-sm font-bold rounded-full ${
                        ranking.rank === 1 ? 'bg-gradient-to-br from-yellow-400 to-amber-500 text-black' :
                        ranking.rank === 2 ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-black' :
                        ranking.rank === 3 ? 'bg-gradient-to-br from-amber-600 to-amber-700 text-white' :
                        'border-2 border-border text-muted-foreground'
                      }`}>
                        {ranking.rank}
                      </div>
                      
                      {/* College Info */}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm truncate">{ranking.college_name}</div>
                        <div className="text-xs text-muted-foreground truncate">{ranking.location}</div>
                      </div>
                      
                      {/* CTC */}
                      <div className="text-right flex-shrink-0">
                        <div className="font-mono font-bold text-sm text-emerald-600 dark:text-emerald-400">
                          {formatCurrency(ranking.avg_ctc)}
                        </div>
                        <div className="text-[10px] text-muted-foreground">Avg CTC</div>
                      </div>
                    </div>
                    
                    {/* Bottom row */}
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border/50">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 text-[10px] font-medium">
                        {ranking.program_short}
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-secondary text-[10px] font-medium">
                        {ranking.type}
                      </span>
                      <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                        {ranking.placement_count} reports
                      </span>
                    </div>
                  </motion.div>
                ))}
                
                {/* Login prompt for mobile */}
                {!showFullRankings && filteredRankings.length > FREE_RANKINGS_LIMIT && (
                  <div className="relative">
                    {/* Blurred cards */}
                    {filteredRankings.slice(FREE_RANKINGS_LIMIT, FREE_RANKINGS_LIMIT + 3).map((ranking, index) => (
                      <div
                        key={`blur-${ranking.college_id}`}
                        className="p-4 rounded-xl border border-border bg-background mb-3 blur-[6px] pointer-events-none"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-secondary" />
                          <div className="flex-1">
                            <div className="h-4 w-32 bg-secondary rounded mb-2" />
                            <div className="h-3 w-24 bg-secondary rounded" />
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {/* Login overlay */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center p-6 rounded-2xl bg-background/95 backdrop-blur-sm border border-border shadow-lg">
                        <Lock className="h-8 w-8 mx-auto text-primary mb-3" />
                        <p className="font-semibold mb-1">Sign in to see all rankings</p>
                        <p className="text-xs text-muted-foreground mb-4">
                          {filteredRankings.length - FREE_RANKINGS_LIMIT} more results
                        </p>
                        <Button onClick={() => login('/dashboard')} className="w-full rounded-full">
                          Sign in with Google
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Confidence Legend */}
          <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-secondary border border-border rounded-2xl">
            <div className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider mb-2 sm:mb-3">Confidence Score</div>
            <div className="flex flex-wrap gap-3 sm:gap-6 text-xs sm:text-sm">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="confidence-badge confidence-high">
                  <ShieldCheck className="h-3 w-3" />
                  70%+
                </div>
                <span className="text-muted-foreground">High</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="confidence-badge confidence-medium">
                  <Info className="h-3 w-3" />
                  50-69%
                </div>
                <span className="text-muted-foreground">Medium</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="confidence-badge confidence-low">
                  <AlertTriangle className="h-3 w-3" />
                  &lt;50%
                </div>
                <span className="text-muted-foreground">Low</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Floating Action Button */}
      <FloatingActionButton />
    </div>
  );
};

export default Dashboard;
